import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, interval, Observable, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { CustomersService } from '../../../customers/customers.service';
import { CassAccount, CassAccountsService } from '../../../data/cass-accounts.service';
import { SideNavService } from '../../../layout/side-nav.service';
import { ListBaseComponent } from '../../../shared/components/list-base/list-base.component';
import { ColWidths } from '../../../shared/components/list-base/list.constants';
import { CassAccountWarningsCellComponent } from './cass-account-warnings-cell.component';
import { CassAccountWarningsDialogComponent, ICassAccountWarningsData } from './cass-account-warnings-dialog.component';
import { CassAccountRegenerateCellComponent } from './cass-account-regenerate-cell.component';
import { CassAccountTypeCellComponent } from './cass-account-type-cell.component';
import { CassAccountTypeChangeDialogComponent, ICassAccountTypeChangeData } from './cass-account-type-change-dialog.component';


const customFilterOptions = {
  suppressMenu: true,
  floatingFilterComponentParams: { suppressFilterButton: true },
  floatingFilterComponent: 'ListBaseFilterComponent',
};

const formatDate = (field: string) => (params) => {
  const value = params.data && params.data[field];
  return value ? moment(value).format('MM/DD/YYYY') : '';
};

const REGENERATION_POLL_INTERVAL = 5000;
const PENDING_STATUS = 'Pending';
const SUCCESS_STATUS = 'Success';
const FAILED_STATUS = 'Failed';
const DISTRIBUTOR_TYPE = 'Distributor';
const SUPPLIER_TYPE = 'Supplier';


@Component({
  selector: 'exa-cass-accounts-pg',
  templateUrl: './cass-accounts-pg.component.html',
  styleUrls: ['./cass-accounts-pg.component.scss'],
})
export class CassAccountsPgComponent extends ListBaseComponent<CassAccount> implements OnInit, OnDestroy {
  filterModelMap: {[key: string]: string} = {
    account_number: 'account_number',
    account_name: 'account_name',
    vendor_name: 'vendor_name',
    site_number: 'site_number',
    service_types: 'service_types',
    account_state: 'account_state',
  };

  sortModelMap: {[key: string]: string} = {
    account_number: 'account_number',
    account_name: 'account_name',
    vendor_name: 'vendor_name',
    site_number: 'site_number',
    service_types: 'service_types',
    account_state: 'account_state',
    type: 'type',
    conflicts_count: 'conflicts_count',
    account_effective_date: 'account_effective_date',
    account_inactive_date: 'account_inactive_date',
    action: 'last_regenerated_on',
  };

  displayedColumns: any[] = [
    {
      headerName: 'Account Number',
      field: 'account_number',
      minWidth: ColWidths.medium,
      sortable: true,
      filter: 'agTextColumnFilter',
      ...customFilterOptions,
    },
    {
      headerName: 'Account Name',
      field: 'account_name',
      minWidth: ColWidths.normal,
      maxWidth: ColWidths.xLarge,
      sortable: true,
      filter: 'agTextColumnFilter',
      ...customFilterOptions,
    },
    {
      headerName: 'Vendor Name',
      field: 'vendor_name',
      minWidth: ColWidths.large,
      sortable: true,
      filter: 'agTextColumnFilter',
      ...customFilterOptions,
    },
    {
      headerName: 'Site Number',
      field: 'site_number',
      minWidth: ColWidths.small,
      maxWidth: ColWidths.normal,
      sortable: true,
      filter: 'agTextColumnFilter',
      ...customFilterOptions,
    },
    {
      headerName: 'Service Type',
      field: 'service_types',
      minWidth: ColWidths.large,
      sortable: true,
      filter: 'agTextColumnFilter',
      ...customFilterOptions,
    },
    {
      headerName: 'State',
      field: 'account_state',
      minWidth: ColWidths.small,
      sortable: true,
      filter: 'agTextColumnFilter',
      ...customFilterOptions,
    },
    {
      headerName: 'Type',
      field: 'type',
      // fits the 110px type dropdown plus the cell padding
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      cellRenderer: 'CassAccountTypeCellComponent',
    },
    {
      headerName: 'Warnings',
      field: 'conflicts_count',
      minWidth: ColWidths.small,
      sortable: true,
      cellRenderer: 'CassAccountWarningsCellComponent',
    },
    {
      headerName: 'Effective Date',
      field: 'account_effective_date',
      minWidth: ColWidths.normal,
      sortable: true,
      valueGetter: formatDate('account_effective_date'),
    },
    {
      headerName: 'Inactive Date',
      field: 'account_inactive_date',
      minWidth: ColWidths.normal,
      sortable: true,
      valueGetter: formatDate('account_inactive_date'),
    },
    {
      headerName: 'Regeneration',
      field: 'action',
      minWidth: ColWidths.large,
      sortable: true,
      cellRenderer: 'CassAccountRegenerateCellComponent',
    },
  ];

  customerSub: Subscription;
  regenerationSubs: { [accountId: number]: Subscription } = {};
  isEmpty = false;

  constructor(
    public menu: SideNavService,
    private customerService: CustomersService,
    private cassAccountsSvc: CassAccountsService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    auth: AuthService,
  ) {
    super(auth, menu);
  }

  ngOnInit() {
    super.ngOnInit();
    this.gridOptions.frameworkComponents['CassAccountTypeCellComponent'] = CassAccountTypeCellComponent;
    this.gridOptions.frameworkComponents['CassAccountRegenerateCellComponent'] = CassAccountRegenerateCellComponent;
    this.gridOptions.frameworkComponents['CassAccountWarningsCellComponent'] = CassAccountWarningsCellComponent;

    // columns can be resized by dragging the header edge, and a manual size is kept instead of fitting the grid again
    this.gridOptions.defaultColDef = { ...this.gridOptions.defaultColDef, resizable: true } as any;
    this.gridOptions['onColumnResized'] = (event) => {
      if (event.finished && event.source === 'uiColumnDragged') {
        this.resizeToFit = false;
      }
    };

    this.customerSub = this.customerService.selected$.subscribe(() => {
      this.stopAllRegenerationWatches();
      if (this.gridParams) {
        this.onGridReady(this.gridParams);
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.customerSub) {
      this.customerSub.unsubscribe();
    }
    this.stopAllRegenerationWatches();
  }

  getDataObservable(reqParams: any): Observable<{ count: number, results: CassAccount[]; }> {
    const isFirstPage = !reqParams.page || reqParams.page <= 1;

    return this.cassAccountsSvc.list(reqParams).pipe(
      tap(res => {
        if (isFirstPage) {
          this.isEmpty = !res.count;
        }
        (res.results || [])
          .filter(account => account.regeneration_status === PENDING_STATUS)
          .forEach(account => this.watchRegeneration(account.id));
      }),
      map(res => ({
        ...res,
        results: res.results || [],
      })),
    );
  }

  onWarningsClicked(account: CassAccount) {
    const dialogData: ICassAccountWarningsData = {
      accountNumber: account.account_number,
      loadBills: () => this.cassAccountsSvc.getBillTypeConflicts(account.id),
    };

    this.dialog.open(CassAccountWarningsDialogComponent, { data: dialogData, minWidth: '40%' });
  }

  onTypeChanged(account: CassAccount, type: string, revert: () => void) {
    if (account.type === type) {
      return;
    }

    let typeSaved = false;
    // related accounts always take the opposite type
    const relatedType = type === DISTRIBUTOR_TYPE ? SUPPLIER_TYPE : DISTRIBUTOR_TYPE;

    const dialogData: ICassAccountTypeChangeData = {
      accountNumber: account.account_number,
      vendorName: account.vendor_name,
      oldType: account.type,
      newType: type,
      relatedType,
      loadRelatedAccounts: () => this.cassAccountsSvc.getActiveRelatedAccounts(account.id),
      confirmRequest: (relatedAccounts) => this.cassAccountsSvc.updateType(account.id, type).pipe(
        tap(() => {
          typeSaved = true;
          account.type = type;
        }),
        switchMap(() => this.cassAccountsSvc.regenerateBills(account.id, true)),
        tap(() => {
          this.onRegenerationStarted(account);
          relatedAccounts.forEach(relatedAccount => this.onRegenerationStarted({ id: relatedAccount.id, type: relatedType }));
        }),
      ),
    };

    // cancelling without saving puts the dropdown back
    this.dialog.open(CassAccountTypeChangeDialogComponent, { data: dialogData, minWidth: '30%' })
      .afterClosed()
      .subscribe(() => {
        if (!typeSaved) {
          revert();
        }
      });
  }

  // exports what the grid shows: the same filter and sort keys the list request sends, in camelCase for the query string
  exportCsv() {
    const params: { [key: string]: string | boolean } = {};
    const toCamelCase = (key: string) => key.replace(/_([a-z0-9])/g, (_, letter) => letter.toUpperCase());

    const filters = this.gridApi ? this.gridApi.getFilterModel() : {};
    Object.keys(filters).forEach(column => {
      const key = this.filterModelMap[column];
      if (key && filters[column] && filters[column].filter) {
        params[toCamelCase(key)] = filters[column].filter;
      }
    });

    const sort = this.gridApi ? this.gridApi.getSortModel() : [];
    if (sort.length && this.sortModelMap[sort[0].colId]) {
      params.sortBy = this.sortModelMap[sort[0].colId];
      params.isAscending = sort[0].sort === 'asc';
    }

    // window.open skips the customers interceptor, so the selected customer is added here the way the interceptor adds it
    const customer = this.customerService.selectedCustomerSubj.value;
    if (customer) {
      params.customer_id = customer.id;
    }

    this.cassAccountsSvc.exportCsv(params);
  }

  private onRegenerationStarted(account: Partial<CassAccount>) {
    this.updateRow({ ...account, regeneration_status: PENDING_STATUS, last_regenerated_on: new Date().toISOString() });
    this.watchRegeneration(account.id);
  }

  // polls the account while its regeneration is pending, and updates the row once it settles
  private watchRegeneration(accountId: number) {
    if (this.regenerationSubs[accountId]) {
      return;
    }

    this.regenerationSubs[accountId] = interval(REGENERATION_POLL_INTERVAL).pipe(
      switchMap(() => this.cassAccountsSvc.get(accountId).pipe(catchError(() => EMPTY))),
    ).subscribe(account => {
      if (!account) {
        this.stopRegenerationWatch(accountId);
        return;
      }

      // keep the row on Pending until the backend reports a final status
      if (account.regeneration_status === SUCCESS_STATUS) {
        this.stopRegenerationWatch(accountId);
        this.updateRow(account);
        this.toastr.success(`Bills regenerated for account ${account.account_number}`);
      } else if (account.regeneration_status === FAILED_STATUS) {
        this.stopRegenerationWatch(accountId);
        this.updateRow(account);
        this.toastr.error(`Bills regeneration failed for account ${account.account_number}`);
      }
    });
  }

  private stopRegenerationWatch(accountId: number) {
    const sub = this.regenerationSubs[accountId];
    if (sub) {
      sub.unsubscribe();
      delete this.regenerationSubs[accountId];
    }
  }

  private stopAllRegenerationWatches() {
    Object.keys(this.regenerationSubs).forEach(accountId => this.stopRegenerationWatch(+accountId));
  }

  private updateRow(account: Partial<CassAccount>) {
    if (!this.gridApi) {
      return;
    }

    this.gridApi.forEachNode(node => {
      if (node.data && node.data.id === account.id) {
        node.setData({ ...node.data, ...account });
        // the action column has no field value, so ag-grid won't redraw it on data change unless forced
        this.gridApi.refreshCells({ rowNodes: [node], columns: ['action'], force: true });
      }
    });
  }
}
